/**
 * Click-and-drag panning for the category navigation card strip.
 *
 * The strip is a native `overflow-x: auto` scroller, so a trackpad, a touch
 * swipe and the scrollbar already move it — but a mouse press-and-drag does
 * nothing, which is the one gesture people reach for on a card carousel.
 *
 * Touch and pen are deliberately left to the browser: native panning has
 * momentum and rubber-banding that this cannot match. Only mouse pointers are
 * handled here.
 */
(() => {
  const STRIP_SELECTOR = '.category-navigation-cards__grid';
  const READY_ATTRIBUTE = 'data-drag-scroll';
  /* A press has to travel this far before it counts as a drag, so a slightly
     unsteady click still opens the card underneath. */
  const DRAG_THRESHOLD = 6;

  const attach = (strip) => {
    if (strip.hasAttribute(READY_ATTRIBUTE)) return;
    strip.setAttribute(READY_ATTRIBUTE, '');

    let activePointerId = null;
    let startX = 0;
    let startScrollLeft = 0;
    let isDragging = false;
    let suppressNextClick = false;

    const canScroll = () => strip.scrollWidth - strip.clientWidth > 1;

    const refreshAffordance = () => {
      strip.classList.toggle('is-draggable', canScroll());
    };

    const stopDragging = () => {
      if (activePointerId !== null && strip.hasPointerCapture(activePointerId)) {
        strip.releasePointerCapture(activePointerId);
      }
      activePointerId = null;
      isDragging = false;
      strip.classList.remove('is-dragging');
    };

    strip.addEventListener('pointerdown', (event) => {
      if (event.pointerType !== 'mouse' || event.button !== 0 || !canScroll()) return;

      activePointerId = event.pointerId;
      startX = event.clientX;
      startScrollLeft = strip.scrollLeft;
      isDragging = false;
      suppressNextClick = false;
    });

    strip.addEventListener('pointermove', (event) => {
      if (event.pointerId !== activePointerId) return;

      const travelled = event.clientX - startX;

      if (!isDragging) {
        if (Math.abs(travelled) < DRAG_THRESHOLD) return;
        isDragging = true;
        strip.classList.add('is-dragging');
        /* Capture keeps the gesture alive when the cursor leaves the strip
           mid-drag, which is easy to do on a short row. */
        strip.setPointerCapture(activePointerId);
      }

      strip.scrollLeft = startScrollLeft - travelled;
    });

    strip.addEventListener('pointerup', (event) => {
      if (event.pointerId !== activePointerId) return;
      suppressNextClick = isDragging;
      stopDragging();
    });

    strip.addEventListener('pointercancel', (event) => {
      if (event.pointerId !== activePointerId) return;
      stopDragging();
    });

    /* Without this the browser starts its own link/image drag and the strip
       stops receiving pointermove. */
    strip.addEventListener('dragstart', (event) => {
      if (activePointerId !== null) event.preventDefault();
    });

    /* A drag ends with a click on whichever card is under the cursor. Swallow
       that one click so panning never navigates. The flag is cleared on the
       next press, so a drag that produces no click cannot eat a later one. */
    strip.addEventListener(
      'click',
      (event) => {
        if (!suppressNextClick) return;
        suppressNextClick = false;
        event.preventDefault();
        event.stopPropagation();
      },
      true
    );

    refreshAffordance();

    if (typeof ResizeObserver === 'function') {
      new ResizeObserver(refreshAffordance).observe(strip);
    } else {
      window.addEventListener('resize', refreshAffordance);
    }
  };

  const attachAll = () => document.querySelectorAll(STRIP_SELECTOR).forEach(attach);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', attachAll);
  } else {
    attachAll();
  }

  document.addEventListener('shopify:section:load', attachAll);
})();
